import React, { useState } from 'react';
import api from '../api';

const ExternalPaymentForm = ({ hoaId, hoaName }) => {
  const [lineItems, setLineItems] = useState([
    { type: 'dues', description: '', amount: 0 }
  ]);
  const [propertyIdentifier, setPropertyIdentifier] = useState('');
  const [propertyAddress, setPropertyAddress] = useState('');
  const [payerName, setPayerName] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const paymentTypes = [
    { value: 'dues', label: 'HOA Dues' },
    { value: 'events', label: 'Events' },
    { value: 'late_fee', label: 'Late Fee' },
    { value: 'interest', label: 'Interest' },
    { value: 'fine', label: 'Fine' },
    { value: 'other', label: 'Other' }
  ];

  const addLineItem = () => {
    setLineItems([...lineItems, { type: 'dues', description: '', amount: 0 }]);
  };

  const removeLineItem = (index) => {
    const newItems = lineItems.filter((_, i) => i !== index);
    setLineItems(newItems.length > 0 ? newItems : [{ type: 'dues', description: '', amount: 0 }]);
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...lineItems];
    newItems[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    setLineItems(newItems);
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const calculateProcessingFee = () => {
    const subtotal = calculateSubtotal();
    if (paymentMethod === 'bank') {
      return Math.min(subtotal * 0.008, 5.00);
    } else {
      return (subtotal * 0.029) + 0.30;
    }
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateProcessingFee();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setProcessing(true);
    setError('');

    try {
      // Validate
      if (!payerName || !payerEmail || !propertyIdentifier) {
        throw new Error('Please fill in all required fields');
      }

      if (lineItems.every(item => !item.description || item.amount <= 0)) {
        throw new Error('Please add at least one valid payment item');
      }

      // Create Stripe Checkout Session (just like internal payment system)
      const { data } = await api.post('/api/external-payments/create-session', {
        hoa_id: hoaId,
        line_items: lineItems.filter(item => item.description && item.amount > 0),
        property_identifier: propertyIdentifier,
        property_address: propertyAddress,
        payer_name: payerName,
        payer_email: payerEmail,
        payment_method: paymentMethod
      });

      if (data.success && data.session_url) {
        // Redirect to Stripe Checkout (handles card/ACH natively)
        window.location.href = data.session_url;
      } else {
        throw new Error('Failed to create payment session');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-2">{hoaName}</h2>
        <h3 className="text-xl text-gray-600 mb-6">Make a Payment</h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Line Items */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Items *
            </label>
            {lineItems.map((item, index) => (
              <div key={index} className="flex flex-col sm:flex-row gap-2 items-start sm:items-center mb-3 p-4 bg-gray-50 rounded-lg">
                <select
                  value={item.type}
                  onChange={(e) => updateLineItem(index, 'type', e.target.value)}
                  className="w-full sm:w-auto px-3 py-2 border rounded-lg"
                  required
                >
                  {paymentTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Description"
                  value={item.description}
                  onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                  className="w-full sm:flex-1 px-3 py-2 border rounded-lg"
                  required
                />
                <div className="flex gap-2 w-full sm:w-auto">
                  <input
                    type="number"
                    placeholder="Amount"
                    step="0.01"
                    value={item.amount || ''}
                    onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                    className="flex-1 sm:w-32 px-3 py-2 border rounded-lg"
                    required
                  />
                  {lineItems.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLineItem(index)}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addLineItem}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              + Add Another Item
            </button>
          </div>

          {/* Property Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Property ID/Unit # *
              </label>
              <input
                type="text"
                value={propertyIdentifier}
                onChange={(e) => setPropertyIdentifier(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="e.g., 123 or Unit A"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Property Address (optional)
              </label>
              <input
                type="text"
                value={propertyAddress}
                onChange={(e) => setPropertyAddress(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Write full address"
              />
            </div>
          </div>

          {/* Payer Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Name *
              </label>
              <input
                type="text"
                value={payerName}
                onChange={(e) => setPayerName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email for Receipt *
              </label>
              <input
                type="email"
                value={payerEmail}
                onChange={(e) => setPayerEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
          </div>

          {/* Payment Method Selection - Exactly like internal system */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              💳 Select Payment Method:
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPaymentMethod('bank')}
                className={`flex-1 py-3 px-3 rounded-md text-sm font-medium transition-all ${
                  paymentMethod === 'bank'
                    ? 'bg-green-600 text-white shadow-md'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex flex-col items-center">
                  <span className="text-lg mb-1">🏦</span>
                  <span>Bank (ACH)</span>
                  <span className="text-xs mt-1 opacity-90">0.8% fee (max $5)</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod('card')}
                className={`flex-1 py-3 px-3 rounded-md text-sm font-medium transition-all ${
                  paymentMethod === 'card'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex flex-col items-center">
                  <span className="text-lg mb-1">💳</span>
                  <span>Card</span>
                  <span className="text-xs mt-1 opacity-90">2.9% + $0.30</span>
                </div>
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-2 text-center">
              {paymentMethod === 'bank' 
                ? '✓ Lowest fee option - Processing takes 3-5 business days' 
                : '✓ Instant processing - Higher processing fee'}
            </p>
          </div>

          {/* Total Summary */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal:</span>
              <span className="font-medium">${calculateSubtotal().toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Processing Fee ({paymentMethod === 'bank' ? 'ACH' : 'Card'}):</span>
              <span className="font-medium">${calculateProcessingFee().toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t">
              <span>Total:</span>
              <span>${calculateTotal().toFixed(2)}</span>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={processing}
            className="w-full py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {processing ? (
              <span>Processing...</span>
            ) : (
              <>
                <span>💰</span>
                <span>Continue to Secure Payment (${calculateTotal().toFixed(2)})</span>
              </>
            )}
          </button>
          
          <p className="text-xs text-gray-500 mt-2 text-center">
            You'll be redirected to Stripe to complete your secure payment
          </p>
        </form>
      </div>
    </div>
  );
};

const ExternalPaymentPage = ({ slug }) => {
  const [hoaData, setHoaData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHOAData = async () => {
      try {
        const { data } = await api.get(`/api/public-profile/by-slug/${slug}`);
        setHoaData(data);
      } catch (error) {
        console.error('Error loading HOA data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchHOAData();
  }, [slug]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!hoaData) {
    return <div className="flex items-center justify-center min-h-screen">HOA not found</div>;
  }

  return <ExternalPaymentForm hoaId={hoaData.hoa_id} hoaName={hoaData.name} />;
};

export default ExternalPaymentForm;
export { ExternalPaymentPage };
